
import {
  computed,
  defineComponent,
  nextTick,
  onBeforeUnmount,
  onMounted,
  PropType,
  ref,
  watch,
} from 'vue';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';

const MAP_STYLE: maplibregl.StyleSpecification = {
  version: 8,
  sources: {
    osm: {
      type: 'raster',
      tiles: ['https://tile.openstreetmap.org/{z}/{x}/{y}.png'],
      tileSize: 256,
      attribution: '&copy; OpenStreetMap',
    },
  },
  layers: [
    {
      id: 'osm',
      type: 'raster',
      source: 'osm',
    },
  ],
};

export default defineComponent({
  name: 'don-vi-map-preview',

  props: {
    latitude: {
      type: Number as PropType<number | null>,
      default: null,
    },
    longitude: {
      type: Number as PropType<number | null>,
      default: null,
    },
    label: {
      type: String,
      default: '',
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    emptyText: {
      type: String,
      default: 'Chưa có vị trí để hiển thị trên bản đồ',
    },
  },

  setup(props) {
    const mapContainerRef = ref<HTMLElement | null>(null);
    let map: maplibregl.Map | null = null;
    let marker: maplibregl.Marker | null = null;

    const hasCoordinates = computed(() => {
      const lat = Number(props.latitude);
      const lng = Number(props.longitude);
      return (
        Number.isFinite(lat) &&
        Number.isFinite(lng) &&
        Math.abs(lat) <= 90 &&
        Math.abs(lng) <= 180
      );
    });

    const destroyMap = () => {
      marker?.remove();
      marker = null;
      map?.remove();
      map = null;
    };

    const ensureMap = async () => {
      if (!hasCoordinates.value || !mapContainerRef.value) {
        return;
      }

      await nextTick();

      if (!mapContainerRef.value) {
        return;
      }

      const lng = Number(props.longitude);
      const lat = Number(props.latitude);

      if (!map) {
        map = new maplibregl.Map({
          container: mapContainerRef.value,
          style: MAP_STYLE,
          center: [lng, lat],
          zoom: 14,
          attributionControl: false,
        });
        map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-right');
        map.addControl(new maplibregl.AttributionControl({ compact: true }));
      } else {
        map.setCenter([lng, lat]);
        map.resize();
      }

      if (!marker) {
        marker = new maplibregl.Marker({ color: '#009ef7' })
          .setLngLat([lng, lat])
          .addTo(map);
      } else {
        marker.setLngLat([lng, lat]);
      }

      if (props.label) {
        marker.setPopup(
          new maplibregl.Popup({ offset: 16 }).setText(props.label)
        );
      }
    };

    watch(
      () => [props.latitude, props.longitude, props.label] as const,
      async () => {
        if (!hasCoordinates.value) {
          destroyMap();
          return;
        }
        await ensureMap();
      }
    );

    onMounted(async () => {
      if (hasCoordinates.value) {
        await ensureMap();
      }
    });

    onBeforeUnmount(() => {
      destroyMap();
    });

    return {
      mapContainerRef,
      hasCoordinates,
    };
  },
});
